import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Card, Button, Spin, Checkbox, message, Row, Col, Tag, Space, Alert } from 'antd';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import Box from '@iso/components/utility/box';
import { sprayService } from '@iso/services';
import { getSprayJobStatusLabel, getSprayJobTagColor } from '@iso/constants/sprayJobStates';
import useSprayRequiredSections from '../../../hooks/useSprayRequiredSections';

/**
 * Full-page Cleanup step (replaces LeftoverMixModal / DisposalConfirmationModal /
 * OutstandingTasksModal). Operator confirms cleanup tasks, leftover-mix handling
 * and PPE disposal before transitioning to WAITING_FOR_COMPLETION (sign-off)
 * and finally COMPLETED.
 */
const SprayJobCleanup = () => {
  const { id } = useParams();
  const history = useHistory();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cleanupChecked, setCleanupChecked] = useState([]);
  const [leftoverConfirmed, setLeftoverConfirmed] = useState(false);
  const [disposalConfirmed, setDisposalConfirmed] = useState(false);
  const { sections: req } = useSprayRequiredSections(job?.property_id);

  useEffect(() => {
    let cancelled = false;
    sprayService
      .getDetail(id)
      .then((res) => !cancelled && setJob(res?.spray_job || null))
      .catch(() => !cancelled && message.error('Failed to load spray job'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <LayoutWrapper>
        <div style={{ textAlign: 'center', margin: '60px 0' }}>
          <Spin size="large" />
        </div>
      </LayoutWrapper>
    );
  }
  if (!job) {
    return (
      <LayoutWrapper>
        <Box>Spray job not found.</Box>
      </LayoutWrapper>
    );
  }

  // Cleanup page is the single landing for cleanup + finish (Damien:
  // "Cleanup/final are the same page so it's pointless having them
  // assigned separately"). Filter is just `spray_cleanup` now —
  // legacy 'spraying' tasks moved over to the Spray page filter to
  // match the renamed PermissionTaskModal options ("Prep"/"Spray"/
  // "Cleanup"). If you want a legacy task to appear here, edit it.
  const cleanupTasks = (job.tasks || []).filter(
    (t) => t.mapping_status === 'spray_cleanup',
  );
  const toggleTask = (taskName, checked) => {
    setCleanupChecked((prev) =>
      checked ? [...prev, taskName] : prev.filter((t) => t !== taskName),
    );
  };

  const allTasksChecked =
    cleanupTasks.length === 0 || cleanupChecked.length === cleanupTasks.length;
  // Each cleanup gate is bypassable when the agronomist-admin toggle is off.
  const tasksGate = !req.cleanup_tasks || allTasksChecked;
  const leftoverGate = !req.leftover_mix || leftoverConfirmed;
  const disposalGate = !req.ppe_disposal || disposalConfirmed;
  const canFinish = tasksGate && leftoverGate && disposalGate;

  // Whether a refill needs to be spawned after this job completes. The
  // new model: refill_planned is set server-side iff the zones JSON
  // contains at least one {type:'refill'} marker, so that flag alone is
  // the signal — total_spray_volume is now documentation-only and must
  // NOT gate clone creation (the old `total - water > 0` check would
  // silently drop the refill on jobs where the operator never filled it
  // in).
  const refillNeeded = !!job?.refill_planned;

  const handleFinish = async () => {
    setBusy(true);
    try {
      const waitRes = await sprayService.markAsWaitingForCompletion(id);
      if (waitRes?.code !== '0000') {
        message.error(waitRes?.message || 'Failed to advance job status');
        return;
      }
      const completeRes = await sprayService.completeJob(id);
      if (completeRes?.code !== '0000') {
        message.error(completeRes?.message || 'Failed to complete spray job');
        return;
      }
      if (refillNeeded) {
        // Refill is *pre-planned* in the new model — the refill marker
        // sitting in the zones JSON already tells the backend where to
        // resume and how much water the next leg gets. Skip the legacy
        // pause-zone modal (which only made sense for ad-hoc mid-spray
        // refills) and call refill directly so the operator's journey
        // is a single button press → refill confirm → Prep page for
        // the next leg. Damien's PR #417 follow-up: "the operator
        // should have no understanding of the system creating a new
        // spray job because there is a refill."
        const refillRes = await sprayService.refillSpray(id, null);
        if (refillRes?.code !== '0000') {
          message.error(refillRes?.message || 'Failed to create refill spray job');
          return;
        }
        message.success(refillRes?.message || 'Tank refilled — continue with the new fill');
        const nextJobId = refillRes?.new_job?.id;
        if (nextJobId) {
          history.push(`/spray/${nextJobId}/prepare`);
        } else {
          history.push('/spray');
        }
        return;
      }
      message.success('Spray job completed');
      history.push('/spray');
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to complete spray job');
    } finally {
      setBusy(false);
    }
  };

  return (
    <LayoutWrapper>
      <Box style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>Cleanup: {job.job_name}</h2>
            <Tag color={getSprayJobTagColor(job.status)} style={{ marginTop: 8 }}>
              {getSprayJobStatusLabel(job.status)}
            </Tag>
          </Col>
          <Col>
            <Space>
              <Button onClick={() => history.push('/spray')}>Back</Button>
              <Button
                type="primary"
                disabled={!canFinish}
                loading={busy}
                onClick={handleFinish}
              >
                Finish Job
              </Button>
            </Space>
          </Col>
        </Row>
      </Box>

      {req.cleanup_tasks && (
      <Card title="Cleanup Tasks" style={{ marginBottom: 16 }}>
        {cleanupTasks.length === 0 && (
          <span style={{ color: '#666' }}>No cleanup tasks defined for this job.</span>
        )}
        {cleanupTasks.map((task) => (
          <div key={task.task_name} style={{ marginBottom: 8 }}>
            <Checkbox
              checked={cleanupChecked.includes(task.task_name)}
              onChange={(e) => toggleTask(task.task_name, e.target.checked)}
            >
              {task.task_name}
            </Checkbox>
          </div>
        ))}
      </Card>
      )}

      {req.leftover_mix && (
      <Card title="Leftover Mix" style={{ marginBottom: 16 }}>
        <Alert
          type="info"
          showIcon
          message="Dispose of leftover mix according to the product label and your property's drift / dilution policy."
          // The default info-Alert background ends up as a dark olive on
          // this property's theme (the @primary-color override propagates
          // through antd's auto-colour algorithm), which made the text
          // hard to read. Force a light-green tint with explicit dark
          // text so the green identity is kept but contrast is high.
          style={{
            marginBottom: 12,
            background: '#f0f7f3',
            border: '1px solid #b7d8c5',
            color: '#1f3a2a',
          }}
        />
        <Checkbox
          checked={leftoverConfirmed}
          onChange={(e) => setLeftoverConfirmed(e.target.checked)}
        >
          Leftover mix handled
        </Checkbox>
      </Card>
      )}

      {req.ppe_disposal && (
      <Card title="PPE & Equipment Disposal" style={{ marginBottom: 16 }}>
        <Alert
          type="info"
          showIcon
          message="Rinse and dispose of single-use PPE; clean reusable equipment per the spray plan."
          // See the matching alert in the Leftover Mix card above — explicit
          // light-green styling keeps the green identity readable on this
          // property's themed antd.
          style={{
            marginBottom: 12,
            background: '#f0f7f3',
            border: '1px solid #b7d8c5',
            color: '#1f3a2a',
          }}
        />
        <Checkbox
          checked={disposalConfirmed}
          onChange={(e) => setDisposalConfirmed(e.target.checked)}
        >
          PPE & equipment cleaned/disposed of
        </Checkbox>
      </Card>
      )}

    </LayoutWrapper>
  );
};

export default SprayJobCleanup;
